/**
 * Utility functions for User component
 */

import * as intrfce from "./Interfaces";

/**
 * Options for field validation skip logic
 */
export interface FieldSkipOptions {
    skipFields?: string[];
    skipStateValueForNonUS?: boolean;
    locale?: string;
}

/**
 * Generic form validator that checks all fields in an object
 * @param formData - Object containing form fields to validate
 * @param formType - Type of form (billing/shipping) for validation context
 * @param validateFn - Function to validate individual fields
 * @param validationErrors - Current validation errors object
 * @param skipOptions - Optional configuration for skipping certain fields
 * @returns true if all fields are valid, false otherwise
 */
export const validateFormFields = (
    formData: Record<string, any>,
    formType: "billing" | "shipping",
    validateFn: (fieldName: string, value: any, formType: "billing" | "shipping") => boolean,
    validationErrors: Record<string, string>,
    skipOptions?: FieldSkipOptions
): boolean => {
    if (isObjectEmpty(formData)) {
        return false;
    }

    for (const [fieldName, value] of Object.entries(formData)) {
        // Skip validation for specific fields if configured
        if (skipOptions?.skipFields?.includes(fieldName)) {
            continue;
        }

        // Skip validation for stateValue when locale is not en-us
        if (
            skipOptions?.skipStateValueForNonUS &&
            fieldName === "stateValue" &&
            skipOptions.locale !== "en-us"
        ) {
            continue;
        }

        const fieldIsValid = validateFn(fieldName, value, formType);
        if (!fieldIsValid) {
            console.log(
                `Validation failed for ${fieldName}:`,
                validationErrors[fieldName],
                value
            );
            return false;
        }
    }

    return true;
};

/**
 * Check if billing address has meaningful fields beyond country selection
 * @param formBillingAddress - Billing address form data
 * @param locale - Current locale
 * @returns true if address has fields other than country/state
 */
export const hasMeaningfulAddressFields = (
    formBillingAddress: intrfce.iAddressFormData,
    locale: string
): boolean => {
    return Object.entries(formBillingAddress).some(([key, value]) => {
        // Skip countryName, countryValue, and stateValue (when not en-us) in the check
        if (
            key === "countryName" ||
            key === "countryValue" ||
            (key === "stateValue" && locale !== "en-us")
        ) {
            return false;
        }
        return !isEmptyString(value);
    });
};

/**
 * Checks if a value is an empty string or undefined
 */
export const isEmptyString = (value: any): boolean => {
  return (
    value === undefined || (typeof value === "string" && value.trim() === "")
  );
};

/**
 * Checks if an object is empty (all values are null, empty string, or undefined)
 */
export const isObjectEmpty = (obj: any): boolean => {
  if (obj === null || obj === undefined || typeof obj !== "object") {
    return isEmptyString(obj);
  }
  return Object.values(obj).every(
    (value) => value === null || value === "" || value === undefined
  );
};

/**
 * Checks if an address object is empty
 * Used for validating saved addresses
 */
export const isEmptyAddress = (address: any, addNewAddressText: string): boolean => {
  if (address) {
    const keysToCheck = [
      "city",
      "country",
      "countryName",
      "countrySubdivision",
      "firstName",
      "isDefault",
      "lastName",
      "line1",
      "line2",
      "nickName",
      "phoneNumber",
      "postalCode",
    ];
    return (
      address.id === -1 &&
      keysToCheck.every(
        (key) =>
          address[key] === "" ||
          address[key] === false ||
          address[key] === null ||
          address[key] === undefined ||
          address[key] === addNewAddressText
      )
    );
  } else {
    return false;
  }
};

/**
 * Interface for address builder options
 */
interface AddressBuilderOptions {
  locale: string;
  stateDropdown: boolean;
  countyField: boolean;
}

/**
 * Creates a new empty address placeholder
 */
export const createNewAddressPlaceholder = (
  addressOptions: AddressBuilderOptions,
  addNewAddressText: string
): intrfce.iSavedAddress => {
  const { stateDropdown, countyField, locale } = addressOptions;
  
  return {
    id: -1,
    city: "",
    country: "",
    countryName: "",
    ...((stateDropdown || locale === 'zh-tw') && { 
      countrySubdivision: locale === 'zh-tw' ? "TW" : "" 
    }),
    ...(countyField && { county: "" }),
    firstName: "",
    isDefault: true,
    lastName: "",
    line1: "",
    line2: "",
    nickName: addNewAddressText,
    phoneNumber: "",
    postalCode: "",
  };
};

/**
 * Creates an address object from cart data
 */
export const createAddressFromCartData = (
  cartAddress: any,
  options: AddressBuilderOptions,
  nickName: string = "Order Address"
): intrfce.iSavedAddress => {
  const { locale, stateDropdown, countyField } = options;
  
  return {
    ...cartAddress,
    id: Number(cartAddress.id),
    isDefault: false,
    nickName,
    line2: cartAddress.line2 || "",
    city: cartAddress.city || "",
    country: cartAddress.country || "",
    countryName: cartAddress.countryName || "",
    ...((stateDropdown || locale === 'zh-tw') && {
      countrySubdivision: (locale === 'zh-tw' ? "TW" : cartAddress.countrySubdivision) || "",
    }),
    ...(countyField && {
      county: cartAddress.county || "",
    }),
    firstName: cartAddress.firstName || "",
    lastName: cartAddress.lastName || "",
    line1: cartAddress.line1 || "",
    phoneNumber: cartAddress.phoneNumber || "",
    postalCode: cartAddress.postalCode || "",
  };
};

/**
 * Converts a saved address to form address format
 */
export const savedAddressToFormAddress = (
    address: intrfce.iSavedAddress,
    options: AddressBuilderOptions
): intrfce.iAddressFormData => {
    const { locale, stateDropdown, countyField } = options;

    return {
        pincode: address.postalCode || "",
        address: address.line1 || "",
        line2: address.line2 || "",
        cityValue: address.city || "",
        stateValue: (stateDropdown || locale === 'zh-tw')
            ? (locale === 'zh-tw' ? "TW" : address.countrySubdivision)
            : undefined,
        county: countyField ? address.county || "" : undefined,
        countryName: address.countryName || "",
        countryValue: address.country || "",
    };
};

/**
 * Creates an empty form address
 */
export const createEmptyFormAddress = (
    countryList: Array<{ countryName: string; ISO_CC: string }>
): intrfce.iAddressFormData => {
    return {
        pincode: "",
        address: "",
        line2: "",
        cityValue: "",
        stateValue: "",
        county: "",
        countryName: countryList[0]?.countryName || "",
        countryValue: countryList[0]?.ISO_CC || "",
    };
};

/**
 * Handles adding a new address placeholder to saved addresses if it doesn't exist
 * @param selectedAddress - The currently selected address (string if "reset")
 * @param savedAddresses - Array of saved addresses
 * @param setSavedAddresses - Setter function for saved addresses
 * @param setSelectedAddress - Setter function for selected address
 * @param addressOptions - Options for address creation
 * @param addNewAddressText - Text for the "Add New Address" placeholder
 * @returns The ID of the new address placeholder, or existing selected address ID
 */
export const handleNewAddressPlaceholder = (
    selectedAddress: intrfce.iSavedAddress | "reset" | null,
    savedAddresses: intrfce.iSavedAddress[],
    setSavedAddresses: (addresses: intrfce.iSavedAddress[]) => void,
    setSelectedAddress: (address: intrfce.iSavedAddress | null) => void,
    addressOptions: AddressBuilderOptions,
    addNewAddressText: string
): number | string => {
    if (typeof selectedAddress === "string") {
        const newAddress = createNewAddressPlaceholder(addressOptions, addNewAddressText);
        const existingAddress = savedAddresses.find((addr) => addr.id === -1);

        if (!existingAddress) {
            const updatedAddresses = [newAddress, ...savedAddresses];
            setSavedAddresses(updatedAddresses);
        }

        setSelectedAddress(newAddress);
        return newAddress.id || -1;
    } else if (selectedAddress && selectedAddress.id !== undefined) {
        return selectedAddress.id;
    }

    return -1;
};

/**
 * Creates empty billing form data
 */
export const createEmptyBillingData = (
    emailAddress?: string | null
): intrfce.iBillingFormData => {
    return {
        firstNameBilling: "",
        lastNameBilling: "",
        contactBilling: "",
        emailBilling: emailAddress || "",
      vemailBilling: ""
    };
};

/**
 * Helper to update saved addresses before submit
 */
export const updateAddressWithData = (
    savedAddresses: intrfce.iSavedAddress[],
    addressData: any,
    addNewAddressText: string
): intrfce.iSavedAddress[] => {
    return savedAddresses.map(addr => {
        if (addr.id === -1) {
            return {
                ...addr,
                ...addressData,
                id: -1,
                nickName: addr.nickName || addNewAddressText,
                isDefault: addr.isDefault,
            };
        }
        return addr;
    });
};

/**
 * Creates a temporary address from form data (used in address modals)
 */
export const createTemporaryAddress = (
    formData: intrfce.iFormContainer,
    formShippingAddress: intrfce.iAddressFormData,
    nickName: string = "Order Address"
): intrfce.iSavedAddress => {
    return {
        id: 0,
        firstName: formData.firstNameShiping || "",
        lastName: formData.lastNameShiping || "",
        line1: formShippingAddress.address || "",
        line2: formShippingAddress.line2 || "",
        city: formShippingAddress.cityValue || "",
        countrySubdivision: formShippingAddress.stateValue || "",
        postalCode: formShippingAddress.pincode || "",
        country: formShippingAddress.countryValue || "",
        countryName: formShippingAddress.countryName || "",
        phoneNumber: formData.contactShiping || "",
        nickName,
        isDefault: false,
    };
};
